import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { NzTableQueryParams } from 'ng-zorro-antd/table';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzMessageService } from 'ng-zorro-antd/message';
import { IApiResponseDto, IFilterBody, IPagination, IPolitician, IPostType, ITopic } from '../../../../helpers/general.interface';
import { IGetTiktokPostsData } from '../../Models/ITiktokPost';
import { faEdit, faEye, faComments, faExternalLink, faSearch, faSave, faCancel} from '@fortawesome/free-solid-svg-icons';
import { NzDrawerService } from 'ng-zorro-antd/drawer';
import { ITiktokPostDetailsPutDto } from '../../Models/ITiktokPostDetailsPutDto';
import { TiktokPostDetailsComponent } from '../tiktok-post-details/tiktok-post-details.component';
import { TiktokService } from '../../Services/tiktok.service';
import { SpecialDDLValuesEnum } from '../../../../shared/enums/politician-ddl-values.enum';
import { SpecialDDLOptionsEnum } from '../../../../shared/enums/special-ddl-options.enum';
import { PostTypeEnum } from '../../../../shared/enums/post-type.enum';


@Component({
  selector: 'app-tiktok-posts',
  templateUrl: './tiktok-posts.component.html',
  styleUrls: ['./tiktok-posts.component.scss']
})
export class TiktokPostsComponent implements OnInit {
  filtersForm!: FormGroup;
  politicians: IPolitician[] = [];
  attackedPoliticians: IPolitician[] = [];
  types: IPostType[] = [];
  topics: ITopic[] = [];

  posts: IGetTiktokPostsData[] = [];
  isDataLoading: boolean = false;
  totalCount: number = 40;
  pageSize: number = 10;
  pageNumber: number = 1;
  pageSizeOptions = [10, 25, 100];
  sortBy: string = 'createdAt'; // Default sort field
  sortDir: string = 'desc'; // Default sort direction
  searchVal: string = ''; // Default search value

  // Filter constants
  allPoliticianItem = { id: String(SpecialDDLValuesEnum.ALL), name: SpecialDDLOptionsEnum.ALL };
  unspecifiedPoliticianItem = { id: String(SpecialDDLValuesEnum.UNSPECIFIED), name: SpecialDDLOptionsEnum.UNSPECIFIED };
  
  allTopicItem = { id: SpecialDDLValuesEnum.ALL, name: SpecialDDLOptionsEnum.ALL };
  unSpecifiedTopicItem = { id: SpecialDDLValuesEnum.UNSPECIFIED, name: SpecialDDLOptionsEnum.UNSPECIFIED };
  
  allPostTypeItem = { id: SpecialDDLValuesEnum.ALL, type: SpecialDDLOptionsEnum.ALL };
  unPostTypeItem = { id: SpecialDDLValuesEnum.UNSPECIFIED, type: SpecialDDLOptionsEnum.UNSPECIFIED };

  // icons
  faEdit = faEdit;
  faView = faEye; 
  faComments = faComments;
  faExternalLink = faExternalLink;
  faSearch = faSearch;
  faSave = faSave;
  faCancel = faCancel;


  // Edit cache for inline editing
  editCache: { [key: string]: { edit: boolean; data: IGetTiktokPostsData } } = {};

  inputValue?: string;
  options: string[] = [];
  filteredAttackedPoliticians: any[] = [];


  constructor(
    private formBuilder: FormBuilder,
    private tiktokService: TiktokService,
    private modalService: NzModalService,
    private message: NzMessageService,
    private drawerService: NzDrawerService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.getPoliticians();
    this.getAttackedPoliticians();
    this.getPostTypes();
    this.getTopics();
  }
  
  private initForm(): void {
    this.filtersForm = this.formBuilder.group({
      politician: new FormControl(SpecialDDLValuesEnum.ALL.toString()),
      attackedPolitician: new FormControl(SpecialDDLValuesEnum.ALL.toString()),
      topic: new FormControl(SpecialDDLValuesEnum.ALL),
      postType: new FormControl(SpecialDDLValuesEnum.ALL),
      searchBox: new FormControl('')
    });
  }

  // Initialize the edit cache based on posts data
  updateEditCache(): void {
    this.editCache = {};
    this.posts.forEach(item => {
      const data = { ...item };

      // Ensure the data object has the correct properties
      if (data.attackedPolitician === null) {
        data.attackedPolitician = String(SpecialDDLOptionsEnum.UNSPECIFIED); // Use unspecified option
      }

      if (data.topicId === null) {
        data.topicId = SpecialDDLValuesEnum.UNSPECIFIED; // Use unspecified value
      }

      if (data.typeId === null) {
        data.typeId = SpecialDDLValuesEnum.UNSPECIFIED; // Use unspecified value
      }

      this.editCache[item.id] = {
        edit: false,
        data: data
      };
    });
  }

  startEdit(id: string): void {
    this.editCache[id].edit = true;
  }

  cancelEdit(id: string): void {
    const index = this.posts.findIndex(item => item.id === id);
    this.editCache[id] = {
      data: { ...this.posts[index] },
      edit: false
    };
  }

  saveEdit(id: string, attackedPolitician?: string, typeId?: number, topicId?: number): void {

    const index = this.posts.findIndex(item => item.id === id);
    
    const updateDto: ITiktokPostDetailsPutDto = {
          id: id,
          typeId: typeId === SpecialDDLValuesEnum.UNSPECIFIED ? null : typeId,
          attackedPolitician: attackedPolitician === String(SpecialDDLOptionsEnum.UNSPECIFIED) ? null : attackedPolitician,
          topicId: topicId === SpecialDDLValuesEnum.UNSPECIFIED ? null : topicId
    };
    

    this.tiktokService.updatePostDetails(updateDto).subscribe(
    (response: IApiResponseDto) => {
      // Update local data on success
      if (index !== -1) {
        // Update Type
        if (typeId === SpecialDDLValuesEnum.UNSPECIFIED) {
          this.posts[index].type = null;
          this.posts[index].typeId = null;
        } else if (typeId !== undefined) {
          const selectedType = this.types.find(t => t.id === typeId);
          if (selectedType) {
            // Create or update the type object
            this.posts[index].type = { ...selectedType };
            this.posts[index].typeId = typeId;
          }
        }

        // Update Attacked Politician
        if (attackedPolitician === String(SpecialDDLOptionsEnum.UNSPECIFIED) || !attackedPolitician) {
          this.posts[index].attackedPolitician = null;
        } else if (attackedPolitician !== undefined) {
          // Check if the politician exists in the list (search by name, not id)
          const selectedAttackedPolitician = this.attackedPoliticians.find(
            p => p.name.toLowerCase() === attackedPolitician.toLowerCase()
          );
          
          if (selectedAttackedPolitician) {
            // Existing politician - use the name from the list
            this.posts[index].attackedPolitician = selectedAttackedPolitician.name;
          } else {
            // New politician - use the typed value directly
            this.posts[index].attackedPolitician = attackedPolitician;
            
            // Add the new politician to the attackedPoliticians list
            const newPolitician: IPolitician = {
              id: attackedPolitician, // using name as ID for now
              name: attackedPolitician
            };
            this.attackedPoliticians.push(newPolitician);
          }
        }

        // Update Topic
        if (topicId === SpecialDDLValuesEnum.UNSPECIFIED) {
          this.posts[index].topic = null;
          this.posts[index].topicId = null;
        } else if (topicId !== undefined) {
          const selectedTopic = this.topics.find(t => t.id === topicId);
          if (selectedTopic) {
            // Create or update the topic object
            this.posts[index].topic = { ...selectedTopic };
            this.posts[index].topicId = topicId;
          }
        }
      }

      this.editCache[id].edit = false;
      this.filteredAttackedPoliticians = []; // Clear filtered list after save
      this.message.success('Post updated successfully');
    },
    (error) => {
      console.error('Error updating post:', error);
      this.message.error('Failed to update post');
      // Revert changes in case of error
      this.cancelEdit(id);
    }
  );
  }

  getAllTiktokPosts(paginationParams?: IPagination): void {
    this.isDataLoading = true;
    
    // Create pagination object if not provided
    if (!paginationParams) {
      paginationParams = {
        pageNumber: this.pageNumber,
        pageSize: this.pageSize
      };
    }
    
    // Get filter values
    const pageId = this.filtersForm.value.politician;
    const attackedPolitician = this.filtersForm.value.attackedPolitician;
    const topicId = this.filtersForm.value.topic;
    const postTypeId = this.filtersForm.value.postType;
    const searchValue = this.filtersForm.value.searchBox

    // Use populateFilterObj to create the filter body
    const filtersBody = this.populateFilterObj(
      pageId, 
      attackedPolitician, 
      topicId, 
      postTypeId,
      searchValue
    );


    this.tiktokService.getAllPosts(
      paginationParams,
      this.sortBy,
      this.sortDir,
      this.searchVal,
      filtersBody
    ).subscribe(
      (response) => {
        this.posts = response.data;
        this.pageNumber = response.pageNumber;
        this.pageSize = response.pageSize;
        this.totalCount = response.totalElements;
        this.isDataLoading = false;
        this.pageNumber = paginationParams.pageNumber;
        this.pageSize = paginationParams.pageSize;
        
        // Update edit cache after loading posts
        this.updateEditCache();
      },
      (error) => {
        console.error('Error loading posts:', error);
        this.isDataLoading = false;
        this.message.error('Ndodhi një gabim gjatë ngarkimit të të dhënave');
      }
    );
  }


  onQueryParamsChange(params: NzTableQueryParams): void {

    if (params.sort && params.sort.length > 0) {
      const sort = params.sort[0];
      this.sortBy = sort.key;
      this.sortDir = sort.value === 'ascend' ? 'asc' : 'desc';
    }

    let paginationParams: IPagination = {
      pageNumber: params.pageIndex,
      pageSize: params.pageSize
    };
    
    this.getAllTiktokPosts(paginationParams);
  }

  public search(): void {
    // Reset to first page when searching
    let paginationParams: IPagination = {
      pageNumber: 1,
      pageSize: this.pageSize
    };
    
    this.getAllTiktokPosts(paginationParams);
  }


  public getPoliticians() {
    this.tiktokService.getPoliticians().subscribe(
      (data: any) => {
        this.politicians = data;
        this.politicians.unshift(this.allPoliticianItem);
      },
      error => {
        console.error('Error loading politicians', error);
        this.message.error('Error loading politicians');
      }
    );
  }

  public getAttackedPoliticians() {
    this.tiktokService.getAttackedPoliticians().subscribe(
      (data: any) => {
        this.attackedPoliticians = data;
        this.attackedPoliticians.unshift(this.allPoliticianItem, this.unspecifiedPoliticianItem);
      },
      error => {
        console.error('Error loading attacked politicians', error);
        this.message.error('Error loading attacked politicians');
      }
    );
  }
  
  public getPostTypes() {
    this.tiktokService.getPostTypes().subscribe(
      (data: any) => {
        this.types = data;
        this.types.unshift(this.allPostTypeItem, this.unPostTypeItem);
      },
      error => {
        console.error('Error loading types', error);
        this.message.error('Error loading types');
      }
    );
  }

  public getTopics() {
    this.tiktokService.getPostTopics().subscribe(
      (data: any) => {
        this.topics = data;
        this.topics.unshift(this.allTopicItem, this.unSpecifiedTopicItem);
      },
      error => {
        console.error('Error loading topics', error);
        this.message.error('Error loading topics');
      }
    );
  }

  openPostDetails(postId: string): void {
    this.drawerService.create<TiktokPostDetailsComponent>({
      nzTitle: 'Tiktok Post Details',
      nzContent: TiktokPostDetailsComponent,
      nzContentParams: {
        postId
      },
      nzWidth: '40%',
      nzClosable: true,
      nzMaskClosable: true
    });
  }

  // Helper function to get topic name by ID
  getTopicNameById(topicId: number | null): string | null {
    if (topicId === null) return null;
    const topic = this.topics.find(t => t.id === topicId);
    return topic ? topic.name : null;
  }

  // Helper function to get type name by ID
  getTypeNameById(typeId: number | null): string | null {
    if (typeId === null) return null;
    const type = this.types.find(t => t.id === typeId);
    return type ? type.type : null;
  }

  onAttackedPoliticianChange(value: string, postId: string): void {
  // If attackedPolitician is "-1", set typeId to -1
  if (value === String(SpecialDDLValuesEnum.UNSPECIFIED)) {
    this.editCache[postId].data.typeId = SpecialDDLValuesEnum.UNSPECIFIED;
  }
  else {
    // find post type with id 8
    const attackTypeId = PostTypeEnum.POLITICAL_ATTACK_TYPE_ID;
    this.editCache[postId].data.typeId = attackTypeId;
  }
}

onPostTypeChange(value: number, postId: string): void {
  if (value !== PostTypeEnum.POLITICAL_ATTACK_TYPE_ID)
    this.editCache[postId].data.attackedPolitician = String(SpecialDDLOptionsEnum.UNSPECIFIED);
}

// Updated method to handle input and filter suggestions
onAttackedPoliticianInput(value: string): void {
  if (!value) {
    this.filteredAttackedPoliticians = this.attackedPoliticians;
    return;
  }

  // Filter existing politicians based on input
  const filtered = this.attackedPoliticians.filter(politician => 
    politician.name.toLowerCase().includes(value.toLowerCase())
  );

  // Check if the exact value exists
  const exactMatch = filtered.some(politician => 
    politician.name.toLowerCase() === value.toLowerCase()
  );

  // If no exact match, add the new value as an option
  if (!exactMatch && (value.trim() !== '' || value.trim() !== String(SpecialDDLOptionsEnum.UNSPECIFIED))) {
    this.filteredAttackedPoliticians = [
      { name: value, id: null }, // New politician (id is null since it doesn't exist yet)
      ...filtered
    ];
  } else {
    this.filteredAttackedPoliticians = filtered;
  }
}

  private populateFilterObj(profileId: string, attackedPolitician: string, topicId: number, postTypeId: number, searchVal: string): IFilterBody[] {
    let filtersBody: IFilterBody[] = [];

    if (profileId != null && profileId != undefined && profileId != SpecialDDLValuesEnum.ALL.toString())
      filtersBody.push({ propertyName: 'profileId', value: profileId});
  
    if (attackedPolitician != null && attackedPolitician != undefined && attackedPolitician != SpecialDDLValuesEnum.ALL.toString())
       if(attackedPolitician != SpecialDDLValuesEnum.UNSPECIFIED.toString())
          filtersBody.push({ propertyName: 'attackedPolitician', value: attackedPolitician.toString() });
       else
          filtersBody.push({ propertyName: 'attackedPolitician', value: null });
    
    if (topicId != null && topicId != undefined && topicId != SpecialDDLValuesEnum.ALL)
        if(topicId != SpecialDDLValuesEnum.UNSPECIFIED)
           filtersBody.push({ propertyName: 'topicId', value: topicId.toString()});
        else
           filtersBody.push({ propertyName: 'topicId', value: null});
      
    if (postTypeId != null && postTypeId != undefined && postTypeId != SpecialDDLValuesEnum.ALL)
        if(postTypeId != SpecialDDLValuesEnum.UNSPECIFIED)
            filtersBody.push({ propertyName: 'typeId', value: postTypeId.toString()});
        else
            filtersBody.push({ propertyName: 'typeId', value: null});
      
    if (searchVal != null && searchVal.trim() !== '') {
      filtersBody.push({ propertyName: 'description', value: searchVal.trim() });
    }

    return filtersBody;
  }
}